import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { type LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const EVENT_AUTOMATION_COVERAGE_QUERY_KEY = 'survey/eventRegistration/<id>/automationCoverage';

export function useEventAutomationCoverage({
	surveyId,
	automationTemplateId,
	validResponses,
	impersonationContext,
	enabled = true,
}: {
	surveyId?: string;
	automationTemplateId?: string;
	validResponses?: Api.EventRegistrationResponseStatus[];
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();

	return useQuery<Api.IEventAutomationCoverage>({
		enabled: enabled && !!surveyId,
		queryKey: [
			EVENT_AUTOMATION_COVERAGE_QUERY_KEY,
			surveyId,
			automationTemplateId ?? null,
			validResponses ?? null,
			impersonationContext?.account?.id ?? null,
			impersonationContext?.user?.id ?? null,
		],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IEventAutomationCoverage>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `survey/eventRegistration/${encodeURIComponent(surveyId)}/automationCoverage`,
					queryParams: { automationTemplateId, validResponses },
				}),
				'GET'
			),
		staleTime: 30 * 1000,
		// Enrollment runs in a background job after each registration, so the count keeps dropping after load.
		refetchOnWindowFocus: true,
		// No toast: EventAutomationCoverageWarning renders the failure inline with a retry.
		meta: {
			errorLogging: { category: 'EventAutomationCoverage', action: 'Load' },
		} satisfies LevStandardQueryMeta,
	});
}

export const invalidateEventAutomationCoverage = () => {
	reactQueryClient.invalidateQueries({ queryKey: [EVENT_AUTOMATION_COVERAGE_QUERY_KEY] });
};
